import { useState, FormEvent, useMemo, useEffect } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { CustomizeBySearchTypes } from "@enums/customize-by-search-types";
import { LayoutTypes } from "@enums/layout-types";
import { ButtonTypes } from "@enums/button-types";
import { TimeRangeTypes } from "@enums/time-range-types";
import { MONTH_DAY_YEAR_INPUT_DATE_FORMAT } from "@constants/date-formats";
import { SelectOption } from "@idw-types/common";
import { AdaptiveAuthReportConfig } from "@idw-types/data/adaptive-auth-report-config";
import { AdaptiveAuthWorkflowConfig } from "@idw-types/data/adaptive-auth-workflow-config";
import { Company, RelyingParty } from "@idw-types/data/reference-data/generic";
import { RpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { AdaptiveAuthenticationSearchFiltersState } from "@idw-types/state/adaptive-authentication-state";
import { initialAdaptiveAuthenticationState } from "@redux/reducers/adaptive-authentication-reducer";
import { getTimeRange, getTimeRangeLabel } from "@utils/date-util";
import {
    mapRpServiceToSelectOption,
    mapRpServicesToSelectOptions,
    mapEntityWithNameAndIdToSelectOption
} from "@utils/select-option-mapping-util";
import { sortStrings } from "@utils/string-util";
import { ButtonWithSpinner } from "../../../common/button-with-spinner/button-with-spinner";
import { OrganizationSelectFormGroup } from "../../../common/select-form-groups";
import { SearchFormFilterRow } from "../../../common/search-form-filter-row/search-form-filter-row";
import { SelectFormGroup } from "../../../common/select-form-group/select-form-group";
import { RadioInputFormGroup } from "../../../common/radio-input-form-group/radio-input-form-group";
import { TimeRangeSearchFormFilterRow } from "../../../common/time-range-search-form-filter-row/time-range-search-form-filter-row";
import { SearchFormHeaderContainer } from "../../../common/search-form-header-container/search-form-header-container";
import styles from "./adaptive-authentication-search-form.less";

type AdaptiveAuthenticationSearchFormProps = {
    requestInProgress: boolean;
    adaptiveAuthReportConfigs: Array<AdaptiveAuthReportConfig>;
    companies: Array<Company>;
    relyingParties: Array<RelyingParty>;
    rpServices: Array<RpServiceConfiguration>;
    workflows: Array<AdaptiveAuthWorkflowConfig>;
    searchFilters: AdaptiveAuthenticationSearchFiltersState;
    handleSearch: (searchFilters: AdaptiveAuthenticationSearchFiltersState) => void;
};

type AdaptiveAuthenticationSearchFormState = {
    searchType: CustomizeBySearchTypes;
    selectedCompanyOption: SelectOption;
    selectedRpServiceOption: SelectOption;
    selectedWorkflowOption: SelectOption;
    selectedTimeRange: SelectOption;
    startDate: Date;
    endDate: Date;
};

export const AdaptiveAuthenticationSearchForm = (props: AdaptiveAuthenticationSearchFormProps) => {
    const maxDate = useMemo(() => new Date(), []);
    const minDate = useMemo(() => new Date(2015, 0, 1), []);
    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const {
        requestInProgress,
        adaptiveAuthReportConfigs,
        companies,
        relyingParties,
        rpServices,
        workflows,
        searchFilters,
        handleSearch
    } = props;

    const getFilterStateFromSearchFilters = (searchFilters: AdaptiveAuthenticationSearchFiltersState): AdaptiveAuthenticationSearchFormState => {
        const selectedCompany = companies.find(company => searchFilters.companyId === company.id);
        const selectedRpService = rpServices.find(rpService => searchFilters.rpServiceId === rpService.id);
        const selectedWorkflow = workflows.find(workflow => searchFilters.workflowId === workflow.id);

        return {
            searchType: searchFilters.customizeBySearchType,
            selectedCompanyOption: selectedCompany ? mapEntityWithNameAndIdToSelectOption(selectedCompany) : null,
            selectedRpServiceOption: selectedRpService ? mapRpServiceToSelectOption(selectedRpService) : null,
            selectedWorkflowOption: selectedWorkflow ? mapEntityWithNameAndIdToSelectOption(selectedWorkflow) : null,
            selectedTimeRange: {
                label: getTimeRangeLabel(searchFilters.timeRangeType),
                value: searchFilters.timeRangeType
            },
            startDate: searchFilters.startDate,
            endDate: searchFilters.endDate
        };
    };

    const [showFilters, setShowFilters] = useState(true);
    const [filterState, setFilterState] = useState<AdaptiveAuthenticationSearchFormState>(getFilterStateFromSearchFilters(searchFilters));

    useEffect(() => {
        setFilterState(getFilterStateFromSearchFilters(searchFilters));
    }, [searchFilters]);

    const clearFilterState = () => {
        setFilterState(getFilterStateFromSearchFilters(initialAdaptiveAuthenticationState.searchFilters));
    };

    const toggleShowFilters = () => {
        setShowFilters(!showFilters);
    };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        const companyId = filterState.selectedCompanyOption?.value || null;
        const rpServiceId = filterState.searchType === CustomizeBySearchTypes.VERIFICATION_SERVICE ? (filterState.selectedRpServiceOption?.value || null) : null;
        const workflowId = filterState.searchType === CustomizeBySearchTypes.WORKFLOW ? (filterState.selectedWorkflowOption?.value || null) : null;
        handleSearch({
            customizeBySearchType: filterState.searchType,
            companyId,
            rpServiceId,
            workflowId,
            timeRangeType: filterState.selectedTimeRange.value as TimeRangeTypes,
            startDate: filterState.startDate,
            endDate: filterState.endDate,
        });
    };

    const handleUpdateSearchType = (newValue: string) => {
        setFilterState({
            ...filterState,
            searchType: newValue as CustomizeBySearchTypes
        });
    };

    const handleUpdateCompanyOption = (newValue: SelectOption) => {
        setFilterState({
            ...filterState,
            selectedCompanyOption: newValue,
            selectedRpServiceOption: null,
            selectedWorkflowOption: null
        });
    };

    const handleUpdateRpServiceOption = (newValue: SelectOption) => {
        setFilterState({
            ...filterState,
            selectedRpServiceOption: newValue
        });
    };

    const handleUpdateWorkflowOption = (newValue: SelectOption) => {
        setFilterState({
            ...filterState,
            selectedWorkflowOption: newValue
        });
    };

    const handleTimeRangeChange = (newValue: SelectOption) => {
        if (newValue.value === TimeRangeTypes.CUSTOM) {
            setFilterState({
                ...filterState,
                selectedTimeRange: newValue
            });
        } else {
            setFilterState({
                ...filterState,
                selectedTimeRange: newValue,
                ...getTimeRange(newValue.value as TimeRangeTypes)
            });
        }
    };

    const handleStartDateChange = (newStartDate: Date) => {
        setFilterState({
            ...filterState,
            startDate: newStartDate
        });
    };

    const handleEndDateChange = (newEndDate: Date) => {
        setFilterState({
            ...filterState,
            endDate: newEndDate
        });
    };

    const searchTypeOptions: Array<SelectOption> = useMemo(() => {
        return [
            { label: "Workflow", value: CustomizeBySearchTypes.WORKFLOW },
            { label: "Verification Service", value: CustomizeBySearchTypes.VERIFICATION_SERVICE }
        ];
    }, []);

    const rpServiceOptions: Array<SelectOption> = useMemo(() => {
        // get the relying parties based on if a company has been selected
        const validRelyingPartyIds = (filterState.selectedCompanyOption ?
            relyingParties.filter(rp => rp.companyID === filterState.selectedCompanyOption.value) :
            relyingParties
        ).map(rp => rp.id);
        const adaptiveAuthReportConfigServiceIds = adaptiveAuthReportConfigs.map(config => config.rpAXNServiceID);
        // filter the rp services based on the available relying parties
        const validRpServices = rpServices
            .filter(service => validRelyingPartyIds.includes(service.relyingPartyID) && adaptiveAuthReportConfigServiceIds.includes(service.id));

        return mapRpServicesToSelectOptions(validRpServices).sort((a, b) => sortStrings(a.label, b.label));
    }, [filterState.selectedCompanyOption, adaptiveAuthReportConfigs, relyingParties, rpServices]);

    const workflowOptions: Array<SelectOption> = useMemo(() => {
        // get the relying parties based on if a company has been selected
        const validRelyingPartyIds = (filterState.selectedCompanyOption ?
            relyingParties.filter(rp => rp.companyID === filterState.selectedCompanyOption.value) :
            relyingParties
        ).map(rp => rp.id);
        const adaptiveAuthReportConfigServiceIds = adaptiveAuthReportConfigs.map(config => config.rpAXNServiceID);
        // filter the workflows by valid relying party ids
        const finalWorkflows = workflows.filter(workflow => {
            if (validRelyingPartyIds.includes(workflow.relyingPartyID)) {
                // get the primary rp service and check that it exists in the adaptive auth report service ids
                const primaryRpService = workflow.rpServiceWorkflows.find(rpsw => isStringTrue(rpsw.primaryServiceFlag));
                if (primaryRpService) {
                    return adaptiveAuthReportConfigServiceIds.includes(primaryRpService.rpAXNServiceID);
                }
            }
            return false;
        });
        return finalWorkflows.map(workflow => ({ label: workflow.name, value: workflow.id })).sort((a, b) => sortStrings(a.label, b.label));
    }, [filterState.selectedCompanyOption, adaptiveAuthReportConfigs, relyingParties, workflows]);

    const timeRangeTypes = [
        TimeRangeTypes.TODAY,
        TimeRangeTypes.PAST_THREE_DAYS,
        TimeRangeTypes.PAST_SEVEN_DAYS,
        TimeRangeTypes.PAST_FOURTEEN_DAYS,
        TimeRangeTypes.CUSTOM
    ];

    return (
        <Form className={styles.adaptiveAuthenticationSearchForm} onSubmit={handleSubmit}>
            <SearchFormHeaderContainer
                idPrefix={"adaptive-authentication"}
                showFilters={showFilters}
                showToggle={true}
                toggleShowFilters={toggleShowFilters}
            />
            {showFilters && (
                <>
                    <SearchFormFilterRow>
                        <RadioInputFormGroup
                            controlId={"adaptive-authentication-search-filters-search-type-input"}
                            automationId={"adaptive-authentication-search-filters-search-type-input"}
                            disabled={false}
                            isRequired={false}
                            label={resources.common.searchForm.filters.customizeBy.label}
                            onChange={handleUpdateSearchType}
                            options={searchTypeOptions}
                            value={filterState.searchType}
                        />
                    </SearchFormFilterRow>
                    <SearchFormFilterRow>
                        <OrganizationSelectFormGroup
                            idPrefix="adaptive-authentication"
                            axnCompanies={companies}
                            isClearable={true}
                            isRequired={false}
                            layout={layout}
                            isSearchable={true}
                            labelWidth={labelWidth}
                            selectedOption={filterState.selectedCompanyOption}
                            onChange={handleUpdateCompanyOption}
                            disabled={false}
                        />
                        {filterState.searchType === CustomizeBySearchTypes.VERIFICATION_SERVICE && (
                            <SelectFormGroup
                                automationId={`adaptive-authentication-search-filters-service-select`}
                                controlId={`adaptive-authentication-search-filters-service-select`}
                                label={resources.common.searchForm.filters.verificationService.label}
                                ariaLabel={resources.common.searchForm.filters.verificationService.label}
                                onChange={handleUpdateRpServiceOption}
                                options={rpServiceOptions}
                                selectedOption={filterState.selectedRpServiceOption}
                                disabled={false}
                                isRequired={true}
                                isSearchable={true}
                                isClearable={true}
                                labelWidth={labelWidth}
                                layout={layout}
                                noOptionsMessage={() => <span className={styles.redText}>{resources.common.searchForm.filters.verificationService.noOptionsMessage}</span>}
                            />
                        )}
                        {filterState.searchType === CustomizeBySearchTypes.WORKFLOW && (
                            <SelectFormGroup
                                automationId={`adaptive-authentication-search-filters-workflow-select`}
                                controlId={`adaptive-authentication-search-filters-workflow-select`}
                                label={resources.common.searchForm.filters.workflow.label}
                                ariaLabel={resources.common.searchForm.filters.workflow.label}
                                onChange={handleUpdateWorkflowOption}
                                options={workflowOptions}
                                selectedOption={filterState.selectedWorkflowOption}
                                disabled={false}
                                isRequired={true}
                                isSearchable={true}
                                isClearable={true}
                                labelWidth={labelWidth}
                                layout={layout}
                                noOptionsMessage={() => <span className={styles.redText}>{resources.common.searchForm.filters.workflow.noOptionsMessage}</span>}
                            />
                        )}
                    </SearchFormFilterRow>
                    <TimeRangeSearchFormFilterRow
                        idPrefix={"adaptive-authentication-search-filters"}
                        labelWidth={labelWidth}
                        layout={layout}
                        onChangeTimeRange={handleTimeRangeChange}
                        onChangeStartDate={handleStartDateChange}
                        onChangeEndDate={handleEndDateChange}
                        selectedTimeRange={filterState.selectedTimeRange}
                        timeRangeTypes={timeRangeTypes}
                        startDateLabel={resources.common.searchForm.filters.startDate.label}
                        startDateAriaLabel={resources.common.searchForm.filters.startDate.label}
                        endDateLabel={resources.common.searchForm.filters.endDate.label}
                        endDateAriaLabel={resources.common.searchForm.filters.endDate.label}
                        minDate={minDate}
                        maxDate={maxDate}
                        dateFormat={MONTH_DAY_YEAR_INPUT_DATE_FORMAT}
                        startDate={filterState.startDate}
                        endDate={filterState.endDate}
                        showMonthDropdown={true}
                        showYearDropdown={true}
                        showMonthYearPicker={false}

                    />
                    <div className={styles.buttonDiv}>
                        <Button id="adaptive-authentication-search-clear-button" variant={"outline-secondary"} onClick={clearFilterState}>{resources.common.buttons.clear.label}</Button>
                        <ButtonWithSpinner
                            id="adaptive-authentication-search-load-button"
                            disabled={requestInProgress}
                            hideSpinner={false}
                            variant="primary"
                            type={ButtonTypes.SUBMIT}
                            label={resources.common.buttons.load.label}
                        />
                    </div>
                </>
            )}
        </Form>
    );
};